import { forwardRef } from "react";

import {
  BuiSection,
  type IBuiSectionHeaderProps,
  type IBuiSectionProps,
} from "@btnet/blueprint-components";
import { cn } from "@btnet/utilities-core";

export type IBTSectionProps = IBuiSectionProps;

export type IBTSectionHeaderProps = IBuiSectionHeaderProps;

/**
 * BTSection - an app-facing wrapper around `BuiSection` with app-specific styling hooks.
 *
 * Compose with `BTSection.Header` for a header row; anything else is passed as children.
 * Forwards its ref to the underlying `BuiSection` container.
 * @param props - The component props.
 * @param props.className - Additional CSS class names.
 * @param props["data-testid"] - Test ID for the container.
 * @param props.id - Id applied to the container.
 * @param props.style - Inline styles applied to the container.
 */
const BTSectionComponent = forwardRef<HTMLDivElement, IBTSectionProps>(
  (props, ref) => {
    return (
      <BuiSection
        {...props}
        ref={ref}
        className={cn("BTSection", props.className)}
      />
    );
  },
);

BTSectionComponent.displayName = "BTSection";

/**
 * BTSection.Header - a header row for a `BTSection`.
 *
 * An app-facing wrapper around `BuiSection.Header` with app-specific styling hooks.
 * @param props - The component props.
 * @param props.className - Additional CSS class names.
 * @param props["data-testid"] - Test ID for the header.
 */
const BTSectionHeader = (props: IBTSectionHeaderProps) => {
  return (
    <BuiSection.Header
      {...props}
      className={cn("BTSectionHeader", props.className)}
    />
  );
};

/**
 * BTSection - an app-facing wrapper around `BuiSection` with app-specific styling hooks.
 *
 * Compose with `BTSection.Header` for a header row; anything else is passed as children.
 * @param props - The component props.
 * @param props.className - Additional CSS class names.
 * @param props["data-testid"] - Test ID for the container.
 */
export const BTSection = Object.assign(BTSectionComponent, {
  /**
   * BTSection.Header - a header row for a `BTSection`.
   *
   * An app-facing wrapper around `BuiSection.Header` with app-specific styling hooks.
   * @param props - The component props.
   * @param props.className - Additional CSS class names.
   * @param props["data-testid"] - Test ID for the header.
   */
  Header: BTSectionHeader,
});
